import { useState } from 'react';
import type { SellerOrderRow, SellerStage } from '@chicare/shared';

import {
  useAcceptOrder,
  useDispatchOrder,
  usePackOrder,
  useSellerOrder,
  useSellerOrders,
} from '@/lib/consoleQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

const STAGES: Array<{ key: SellerStage; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'accepted', label: 'Preparing' },
  { key: 'packed', label: 'Ready' },
  { key: 'dispatched', label: 'Out' },
];

/**
 * The pharmacy's work queue.
 *
 * ── ONE THING TO DO, AND A BUTTON THAT SAYS IT ──────────────────────────────
 *
 * Every order is at exactly one stage and has exactly one next action. So the
 * card carries one button, labelled with the verb — Accept, Seal, Hand to
 * rider — rather than a menu of things most of which are not currently legal.
 * A pharmacist should never have to work out what to press.
 *
 * ── WHY THE CLOCK IS THE LOUDEST THING ON A NEW ORDER ───────────────────────
 *
 * The accept window is fifteen minutes and a breach is a lost sale plus a buyer
 * who waited for nothing. So a new order shows minutes remaining, it turns red
 * under five, and an overdue one says how far overdue rather than sitting at
 * zero looking merely urgent.
 */
export function OrderQueueScreen() {
  const [stage, setStage] = useState<SellerStage>('new');
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useSellerOrders();

  if (isError) {
    return <AdminError title="We couldn't load your orders" error={error} onRetry={() => refetch()} />;
  }

  const rows = (data?.orders ?? []).filter((order) => order.stage === stage);

  // An empty stage should point at the one that actually has work in it,
  // rather than leaving a pharmacist tapping tabs to find out where it went.
  const busiestStage = STAGES.map(({ key }) => key).reduce<SellerStage | null>(
    (best, key) =>
      (data?.meta.counts?.[key] ?? 0) > (best ? (data?.meta.counts?.[best] ?? 0) : 0) ? key : best,
    null,
  );

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-5 xl:px-8">
      <header className="mb-4">
        <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">Orders</h1>
        <p className="mt-1 text-[13px] text-muted">
          Oldest first. The one at the top is the one to do next.
        </p>
      </header>

      {data && data.meta.breaching_soon > 0 ? (
        <div
          data-testid="breach-warning"
          className="mb-4 flex items-start gap-2.5 rounded-card border border-danger bg-danger-tint p-3.5"
        >
          <Icon name="clock" size={17} className="mt-0.5 shrink-0 text-danger" />
          <p className="m-0 text-[13px] font-medium leading-relaxed text-ink">
            {data.meta.breaching_soon} order{data.meta.breaching_soon === 1 ? '' : 's'} about to pass
            the {data.meta.accept_window_minutes}-minute accept window. Accept them now or the buyer
            gets sent elsewhere.
          </p>
        </div>
      ) : null}

      {/* Scrolls sideways rather than wrapping: four tabs and a count each is
          wider than a 320px screen, and a wrapped tab bar reads as broken. */}
      <div className="-mx-4 mb-4 overflow-x-auto px-4 xl:mx-0 xl:px-0">
        <div className="flex w-max gap-2">
          {STAGES.map(({ key, label }) => {
            const count = data?.meta.counts?.[key] ?? 0;

            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setStage(key);
                  setOpen(null);
                }}
                className={cn(
                  'flex min-h-11 items-center gap-2 whitespace-nowrap rounded-btn border px-3.5 text-[13px] font-medium',
                  stage === key
                    ? 'border-blue bg-blue text-white'
                    : 'border-line bg-white text-muted',
                )}
              >
                {label}
                {count > 0 ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 font-mono text-[11px] font-bold',
                      stage === key ? 'bg-white/25 text-white' : 'bg-paper text-ink',
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" radius="card" />
          <Skeleton className="h-24 w-full" radius="card" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={stage === 'new' ? 'No new orders' : 'Nothing here'}
          body={
            stage === 'new'
              ? 'When a buyer orders from you it appears here straight away.'
              : 'Nothing is at this stage right now.'
          }
          action={
            busiestStage && busiestStage !== stage ? (
              <Button variant="ghost" onClick={() => setStage(busiestStage)}>
                Go to {STAGES.find((s) => s.key === busiestStage)?.label}
              </Button>
            ) : (
              <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
                Check again
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {rows.map((order) => (
            <OrderCard
              key={order.reference}
              order={order}
              open={open === order.reference}
              onToggle={() => setOpen(open === order.reference ? null : order.reference)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({
  order,
  open,
  onToggle,
}: {
  order: SellerOrderRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className={cn('overflow-hidden p-0', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px] font-medium text-ink">{order.reference}</span>
            {order.is_pickup ? <Badge tone="brand">Pickup</Badge> : null}
          </div>

          <p className="m-0 mt-1.5 text-[13.5px] font-semibold text-ink">
            {order.item_count} item{order.item_count === 1 ? '' : 's'} ·{' '}
            {order.buyer_name ?? 'A buyer'}
          </p>

          <p className="m-0 mt-0.5 text-[12.5px] text-muted">
            You receive <b className="font-mono text-ink">{order.you_receive_display}</b>
            <span className="text-faint"> of {order.goods_total_display}</span>
          </p>
        </div>

        <AcceptClock minutesLeft={order.accept_minutes_left} waiting={order.waiting_minutes} />
      </button>

      {open ? <OrderDetail reference={order.reference} /> : null}
    </Card>
  );
}

/**
 * Minutes left, or how far overdue.
 *
 * Never clamped at zero: an order forty minutes past its window has to look
 * different from one with thirty seconds to go, because they need different
 * conversations with the buyer.
 */
function AcceptClock({ minutesLeft, waiting }: { minutesLeft: number | null; waiting: number }) {
  if (minutesLeft === null) {
    return (
      <span className="shrink-0 text-right font-mono text-[11.5px] text-faint">
        {duration(waiting)} ago
      </span>
    );
  }

  const overdue = minutesLeft < 0;
  const urgent = minutesLeft <= 5;

  return (
    <span
      className={cn(
        'shrink-0 rounded-btn px-2 py-1 text-right font-mono text-[12px] font-bold tabular-nums',
        overdue
          ? 'bg-danger text-white'
          : urgent
            ? 'bg-danger-tint text-danger'
            : 'bg-paper text-muted',
      )}
    >
      {overdue ? `${duration(Math.abs(minutesLeft))} over` : `${duration(minutesLeft)} left`}
    </span>
  );
}

/**
 * Minutes, hours or days — whichever a person would actually say.
 *
 * "14067m over" is technically correct and completely unreadable. Nobody counts
 * in four-figure minutes, and a pharmacist glancing at a queue needs the shape
 * of the number, not its precision.
 */
function duration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h`;

  return `${Math.floor(minutes / (60 * 24))}d`;
}

function OrderDetail({ reference }: { reference: string }) {
  const { data, isLoading, isError, error, refetch } = useSellerOrder(reference);

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this order" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const { order, nextAction } = data;

  return (
    <div className="border-t border-line bg-paper p-4">
      {/* The pick list. Stock was allocated FEFO at checkout, so the batch is
          already decided — a pharmacist reaching for the nearest box would make
          the batch number on the buyer's order a lie. */}
      <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
        Pick these exact boxes
      </h3>

      <ul className="m-0 mb-4 list-none space-y-2 p-0">
        {order.items.map((item, index) => (
          <li
            key={`${item.batch_no ?? 'item'}-${index}`}
            className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-card border border-line bg-white p-3"
          >
            <span className="font-mono text-[15px] font-bold tabular-nums text-blue">
              ×{item.quantity}
            </span>
            <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-ink">{item.name}</span>

            {item.is_cold_chain ? <Badge tone="brand">Cold chain</Badge> : null}
            {item.is_pom ? <Badge tone="caution">POM</Badge> : null}

            <span className="w-full font-mono text-[12px] text-muted">
              Batch <b className="text-ink">{item.batch_no ?? '—'}</b>
              {item.expiry ? <span className="text-faint"> · expires {item.expiry}</span> : null}
            </span>
          </li>
        ))}
      </ul>

      {order.deliver_to && !order.is_pickup ? (
        <section className="mb-4">
          <h3 className="m-0 mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            Going to
          </h3>
          <p className="m-0 text-[13px] leading-relaxed text-ink">
            {[
              order.deliver_to.recipient_name,
              order.deliver_to.line1,
              order.deliver_to.line2,
              order.deliver_to.lga,
              order.deliver_to.state,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
        </section>
      ) : null}

      <NextAction reference={order.reference} action={nextAction} sealNumber={order.seal_number} />
    </div>
  );
}

/**
 * One button, labelled with the verb.
 *
 * Each stage has exactly one legal move, so offering a menu would mean offering
 * things that are not currently allowed and letting the pharmacist find out by
 * pressing them.
 */
function NextAction({
  reference,
  action,
  sealNumber,
}: {
  reference: string;
  action: string | null;
  sealNumber: string | null;
}) {
  const accept = useAcceptOrder();
  const pack = usePackOrder();
  const dispatch = useDispatchOrder();

  const [seal, setSeal] = useState(sealNumber ?? '');
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const failure = accept.error ?? pack.error ?? dispatch.error;
  const busy = accept.isPending || pack.isPending || dispatch.isPending;

  if (dispatch.isSuccess) {
    return (
      <div
        data-testid="dispatched"
        className="rounded-card border border-verified bg-verified-tint p-3.5"
      >
        <p className="m-0 text-[13.5px] font-bold text-verified-ink">On its way</p>
        <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-ink">
          The buyer has their delivery code
          {dispatch.data.code_sent_to.length > 0
            ? ` by ${dispatch.data.code_sent_to.join(' and ')}`
            : ''}
          . The rider will need it at the door.
        </p>
      </div>
    );
  }

  return (
    <div>
      {action === 'pack' ? (
        <label className="mb-3 block">
          <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Security seal number</span>
          <input
            value={seal}
            onChange={(event) => setSeal(event.target.value)}
            placeholder="SEAL-0099"
            autoCapitalize="characters"
            className="min-h-12 w-full rounded-btn border border-line bg-white px-3 font-mono text-[15px] text-ink outline-none focus:border-blue"
          />
          <span className="mt-1.5 block text-[11.5px] leading-relaxed text-muted">
            The rider photographs this at pickup and the buyer checks it at the door. Use a fresh
            seal — a number cannot be reused.
          </span>
        </label>
      ) : null}

      {failure ? (
        <p role="alert" className="mb-2 text-[12.5px] font-medium text-danger">
          {failure instanceof Error ? failure.message : 'That did not go through.'}
        </p>
      ) : null}

      {action === 'accept' ? (
        <Button size="lg" disabled={busy} onClick={() => accept.mutate(reference)}>
          {busy ? 'Accepting…' : 'Accept this order'}
        </Button>
      ) : null}

      {action === 'pack' ? (
        <Button
          size="lg"
          disabled={busy || seal.trim().length < 4}
          onClick={() => pack.mutate({ reference, sealNumber: seal.trim() })}
        >
          {busy ? 'Saving…' : 'Sealed and ready'}
        </Button>
      ) : null}

      {action === 'dispatch' ? (
        <>
          <Button
            size="lg"
            disabled={busy}
            onClick={() => dispatch.mutate({ reference, idempotencyKey })}
          >
            {busy ? 'Sending…' : 'Hand to rider'}
          </Button>
          <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-muted">
            Press this as the rider takes the bag. It sends the buyer their delivery code and, on
            orders charged at dispatch, takes the payment.
          </p>
        </>
      ) : null}

      {action === null ? (
        <p className="m-0 text-[13px] text-muted">
          Nothing to do here — this order is with the rider.
        </p>
      ) : null}
    </div>
  );
}
